import { ArrowLeft } from "../../../../common/arrow";
import { ListNotification } from "../../../../notifications/ListNotification";
import { PrimarySpinner } from "../../../../spinner/primary-spinner";
import settings from "../../../../../img/edit_icon.svg";
import React, { useRef } from "react";
import { useIsMobile } from "../../../../../hooks/state-checkers/use-is-mobile";
import { FormattedMessage } from "react-intl";
import { useQuery, useApolloClient } from "@apollo/client";
import { NOTIFICATION_SUBSCRIPTION } from "../subscriptions";
import {
  GET_NOTIFICATIONS,
  GET_COURSE_TEACHINGS,
  GET_USER_HEADER,
} from "../../../../../apollo-client/queries";
import { NOTIFICATION_TYPES } from "../../../../../notification-constants";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery,
  List,
} from "@mui/material";
import { styled } from "@mui/material/styles";

export const HeaderNotificationsBox = ({
  setIsNotificationsMenuOpen,
  setIsEmailMenuOpen,
}) => {
  const isMobile = useIsMobile();
  const client = useApolloClient();
  const theme = useTheme();
  const isMobileMUI = useMediaQuery(theme.breakpoints.down("lg"));

  const {
    data: dataNotifications,
    loading: loadingNotifications,
    error: errorNotifications,
    fetchMore: fetchMoreNotifications,
    subscribeToMore: subscribeToMoreNotifications,
  } = useQuery(GET_NOTIFICATIONS, {
    variables: { limit: 15 },
  });

  subscribeToMoreNotifications({
    document: NOTIFICATION_SUBSCRIPTION,
    updateQuery: async (prev, { subscriptionData }) => {
      if (!subscriptionData.data) return prev;

      const newNotification = subscriptionData.data.newNotification;

      if (
        newNotification.notificationType === NOTIFICATION_TYPES.NEW_TEACHING
      ) {
        await client.refetchQueries({ include: [GET_COURSE_TEACHINGS] });
      }

      client.refetchQueries({
        include: [GET_USER_HEADER, GET_NOTIFICATIONS],
      });

      return Object.assign({}, prev, {
        user: {
          ...prev.user,
          notifications: [newNotification, ...prev.user.notifications],
        },
      });
    },
  });

  const scrollRef = useRef(null);

  const handleScroll = async () => {
    const { current } = scrollRef;
    if (
      !loadingNotifications &&
      current.scrollTop + current.clientHeight >= current.scrollHeight &&
      dataNotifications.user.notifications.length <
        dataNotifications.user.notificationsCount
    ) {
      await fetchMoreNotifications({
        variables: {
          limit: 15,
          skip: dataNotifications.user.notifications.length,
        },
      });
    }
  };

  return (
    <NotificationsContainer elevation={3}>
      <NotificationsHeader>
        {isMobile && (
          <BackButton onClick={() => setIsNotificationsMenuOpen(false)}>
            <ArrowLeft />
          </BackButton>
        )}
        <NotificationsTitle>
          <FormattedMessage id="abo-notifications.notifications" />
        </NotificationsTitle>
      </NotificationsHeader>

      <NotificationsList ref={scrollRef} onScroll={handleScroll}>
        {!errorNotifications &&
          dataNotifications &&
          dataNotifications?.user?.notifications && (
            <List sx={{ p: 0 }}>
              {dataNotifications.user.notifications.map(
                (notification, i, notifications) => (
                  <ListNotification
                    key={"notification_" + i}
                    notification={notification}
                    prevNotification={notifications[i - 1]}
                    setIsNotificationsMenuOpen={setIsNotificationsMenuOpen}
                    isLast={i === notifications.length - 1}
                  />
                )
              )}
            </List>
          )}
        {loadingNotifications && (
          <LoadingSpinnerContainer>
            <PrimarySpinner />
          </LoadingSpinnerContainer>
        )}
      </NotificationsList>

      {loadingNotifications && (
        <FullPageLoadingSpinner>
          <PrimarySpinner />
        </FullPageLoadingSpinner>
      )}

      {!errorNotifications &&
        dataNotifications?.user?.notifications?.length === 0 && (
          <EmptyNotificationsMessage>
            <Typography>
              Abonniere Module komplett, oder Teilbereiche, oder stelle Fragen
              und lass sie beantworten
            </Typography>
          </EmptyNotificationsMessage>
        )}

      <EmailSettingsButton onClick={() => setIsEmailMenuOpen(true)}>
        <EmailSettingsIcon src={settings} />
        <FormattedMessage id="notifications.emailSettings" />
      </EmailSettingsButton>
    </NotificationsContainer>
  );
};

// Styled Components
const NotificationsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "477px",
  height: "500px",
  overflow: "hidden",
  [theme.breakpoints.down("lg")]: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
}));

const NotificationsHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  paddingLeft: "10px",
  [theme.breakpoints.down("lg")]: {
    padding: "16px",
    borderBottom: "1px solid #e0e0e0",
  },
}));

const BackButton = styled(IconButton)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    marginRight: "12px",
  },
}));

const NotificationsTitle = styled(Typography)(({ theme }) => ({
  margin: "15px",
  fontWeight: 600,
  fontSize: "19px",
  lineHeight: "26px",
  color: "#383838",
  [theme.breakpoints.down("lg")]: {
    margin: "0",
    fontSize: "20px",
    lineHeight: "28px",
  },
}));

const NotificationsList = styled(Box)(({ theme }) => ({
  overflowY: "scroll",
  overflowX: "hidden",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("lg")]: {
    padding: "0",
  },
  "&::-webkit-scrollbar-track": {
    borderRadius: "10px",
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar": {
    width: "10px",
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: "10px",
    backgroundColor: "transparent",
  },
  "&:hover": {
    "&::-webkit-scrollbar-track": {
      WebkitBoxShadow: "transparent",
      borderRadius: "10px",
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar": {
      width: "10px",
    },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: "10px",
      WebkitBoxShadow: "transparent",
      backgroundColor: "lightgrey",
    },
  },
}));

const LoadingSpinnerContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "50px",
});

const FullPageLoadingSpinner = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
});

const EmptyNotificationsMessage = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  margin: "0 auto",
  width: "70%",
  height: "100%",
});

const EmailSettingsButton = styled(Box)(({ theme }) => ({
  marginTop: "auto",
  display: "flex",
  alignItems: "center",
  padding: "10px",
  borderTop: "solid 1px #a7a6a6",
  fontWeight: 500,
  fontSize: "18px",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
  [theme.breakpoints.down("lg")]: {
    padding: "16px",
    fontSize: "16px",
    borderTop: "solid 1px #e0e0e0",
  },
}));

const EmailSettingsIcon = styled("img")({
  width: "20px",
  height: "20px",
  marginRight: "10px",
});
